import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders, apiCall } from "../api";

/**
 * Administration -> HR Export (Monthly JSON).
 *
 * One JSON file per completed calendar month, for upload into the HR software.
 * The server owns every rule — the month window, the 2nd-of-next-month cutoff,
 * what counts as master data — and this page only renders what it reports. The
 * availability flag and the "available from" date come from
 * /hr-export/monthly/months, computed in IST on the server, so the button state
 * never depends on the clock or timezone of the machine the browser is on.
 *
 * The file is not stored anywhere: each download rebuilds it from the database,
 * which is how a correction typed in November for a September date still reaches
 * September's file.
 */

function monthLabel(m) {
  return `${m.month_name} ${m.year}`;
}

export default function HRMonthlyExport() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(null);   // period being fetched
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 6000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setInfo(await apiCall("get", "/hr-export/monthly/months"));
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load the monthly export list.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function download(month) {
    setDownloading(month.period);
    try {
      const resp = await axios.get(`${API}/hr-export/monthly/${month.period}`, {
        headers: getAuthHeaders(),
        responseType: "blob",
        timeout: 120000,
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = month.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast(`Downloaded ${month.filename}`);
    } catch (e) {
      // responseType "blob" means an error body arrives as a Blob, so the
      // server's message has to be read out of it before it can be shown.
      let detail = "";
      try {
        const body = e.response?.data;
        detail = typeof body?.text === "function"
          ? JSON.parse(await body.text())?.detail
          : body?.detail;
      } catch { /* fall through to the generic message */ }
      showToast(detail || `Could not export ${monthLabel(month)}.`, "error");
      // A 409 means our availability view is stale (the page was left open
      // across midnight on the 1st, say). Re-read it so the row corrects itself.
      if (e.response?.status === 409) load();
    } finally {
      setDownloading(null);
    }
  }

  if (loading) {
    return <div className="text-sm text-gray-500 py-6" data-testid="hr-export-loading">Loading monthly exports…</div>;
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm" data-testid="hr-export-error">
        {error}
        <button onClick={load} className="ml-3 underline font-medium">Retry</button>
      </div>
    );
  }

  const months = info?.months || [];
  const pending = months.filter(m => !m.available);

  return (
    <div className="space-y-4" data-testid="hr-monthly-export">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg text-sm text-white max-w-md ${toast.type === "error" ? "bg-red-600" : "bg-green-600"}`}
          data-testid="hr-export-toast"
        >
          {toast.msg}
        </div>
      )}

      {/* How the cutoff works — the rule, in the user's words, on the page itself. */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="hr-export-explainer">
        <h3 className="font-semibold text-gray-800 text-sm">HR Export (Monthly JSON)</h3>
        <p className="text-xs text-gray-700 mt-1">
          One file per calendar month, for upload into the HR software. Each file holds
          only data dated from the <strong>1st to the last day</strong> of that month
          (28, 29, 30 or 31 days — whatever the month actually has), in IST. Nothing
          from outside the month is included.
        </p>
        <ul className="text-xs text-gray-700 mt-2 space-y-1 list-disc pl-4">
          <li>
            A month becomes downloadable on the <strong>2nd of the following month</strong>,
            because the last day's entries are typed up on the next working day — and if
            the 1st is a Sunday, that lands on the 2nd. It stays downloadable from then on.
          </li>
          <li>Only completed months are listed. The current month and future months are never exportable.</li>
          <li>
            Nothing is frozen: the file is rebuilt from the database every time you
            download it, so corrections you enter later for dates <em>inside</em> the
            month are included. The date window itself never widens.
          </li>
          <li>
            Reference data (machines, instruments, departments, users and other
            catalogues) has no date and travels in a separate <code>master_data</code>{" "}
            section, so it cannot be mistaken for the month's activity. Spare-part stock
            is a live figure and is labelled as such inside the file.
          </li>
          <li>
            Each file also carries a <code>previous_month_end_snapshot</code> — the PM
            backlog and incident position as they stood at the close of the{" "}
            <em>previous</em> month, so HR can read the month as a movement on an
            opening balance. It holds figures only, never records, and every figure is
            rebuilt as at that date rather than read live.
          </li>
          <li>Passwords, API keys, tokens and session data are never included.</li>
        </ul>
        {info?.today_ist && (
          <p className="text-xs text-gray-500 mt-2">
            Today in IST: {info.today_ist}. Each download is recorded in the Audit Log.
          </p>
        )}
      </div>

      {pending.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-xs text-amber-800" data-testid="hr-export-pending-note">
          {pending.map(m => (
            <div key={m.period}>{m.message || `${monthLabel(m)} is available from ${m.available_from_display}.`}</div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-xl border overflow-hidden" data-testid="hr-export-table-card">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h4 className="font-semibold text-gray-800 text-sm">Last 12 completed months</h4>
          <p className="text-xs text-gray-500 mt-0.5">Newest first</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Month</th>
                <th className="text-left px-4 py-2 font-medium">Year</th>
                <th className="text-right px-4 py-2 font-medium">Days</th>
                <th className="text-left px-4 py-2 font-medium">Period covered</th>
                <th className="text-right px-4 py-2 font-medium">File</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {months.map(m => (
                <tr key={m.period} className={m.available ? "hover:bg-gray-50" : "bg-gray-50/60"} data-testid={`hr-export-row-${m.period}`}>
                  <td className="px-4 py-2 font-medium text-gray-800">{m.month_name}</td>
                  <td className="px-4 py-2 text-gray-700">{m.year}</td>
                  <td className="px-4 py-2 text-right text-gray-700" data-testid={`hr-export-days-${m.period}`}>{m.days_in_month}</td>
                  <td className="px-4 py-2 text-gray-500 text-xs whitespace-nowrap">
                    {m.period_start} &rarr; {m.period_end}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {m.available ? (
                      <button
                        onClick={() => download(m)}
                        disabled={downloading === m.period}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                        data-testid={`hr-export-download-${m.period}`}
                      >
                        {downloading === m.period && (
                          <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        )}
                        {downloading === m.period ? "Building…" : "Download"}
                      </button>
                    ) : (
                      <button
                        disabled
                        title={m.message || ""}
                        className="px-3 py-1.5 bg-gray-200 text-gray-500 rounded-lg text-xs font-medium cursor-not-allowed"
                        data-testid={`hr-export-unavailable-${m.period}`}
                      >
                        Available from {m.available_from_display}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {months.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500 text-sm">No completed months yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
